/**
 * Scheduled Reports admin page — create/edit scheduled reports,
 * view list with next/last delivery dates and status.
 * Admin sees all users' reports; non-admin sees own only.
 *
 * Requirements: 54.7, 54.8, 54.12
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';

// ─── Types ─────────────────────────────────────────────────────────────

type ReportType = 'financial' | 'itSpend';
type ScheduleFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly';
type ReportFormat = 'csv' | 'pdf' | 'both';

interface ScheduledReport {
  reportId: number;
  userId: string;
  userEmail: string;
  reportType: ReportType;
  reportName: string;
  schedule: ScheduleFrequency;
  format: ReportFormat;
  recipients: string[];
  reportParams: Record<string, unknown>;
  isActive: boolean;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: 'success' | 'failure' | null;
  nextDeliveryAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ScheduledReportFormData {
  reportType: ReportType;
  reportName: string;
  schedule: ScheduleFrequency;
  format: ReportFormat;
  recipients: string;
}

// ─── Component ─────────────────────────────────────────────────────────

export function ScheduledReports() {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [formData, setFormData] = useState<ScheduledReportFormData>({
    reportType: 'financial',
    reportName: '',
    schedule: 'weekly',
    format: 'csv',
    recipients: '',
  });
  const [saving, setSaving] = useState(false);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<ScheduledReport[]>('admin/scheduled-reports');
      setReports(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  function openCreateForm() {
    setEditingReport(null);
    setFormData({
      reportType: 'financial',
      reportName: '',
      schedule: 'weekly',
      format: 'csv',
      recipients: '',
    });
    setShowForm(true);
  }

  function openEditForm(report: ScheduledReport) {
    setEditingReport(report);
    setFormData({
      reportType: report.reportType,
      reportName: report.reportName,
      schedule: report.schedule,
      format: report.format,
      recipients: report.recipients.join(', '),
    });
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);

    try {
      const recipientList = formData.recipients
        .split(',')
        .map(r => r.trim())
        .filter(Boolean);

      if (editingReport) {
        await apiClient.put(`admin/scheduled-reports/${editingReport.reportId}`, {
          reportName: formData.reportName,
          schedule: formData.schedule,
          format: formData.format,
          recipients: recipientList,
        });
      } else {
        await apiClient.post('admin/scheduled-reports', {
          reportType: formData.reportType,
          reportName: formData.reportName,
          schedule: formData.schedule,
          format: formData.format,
          recipients: recipientList,
        });
      }

      setShowForm(false);
      await loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save report');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(reportId: number) {
    if (!confirm('Are you sure you want to delete this scheduled report?')) return;

    try {
      await apiClient.delete(`admin/scheduled-reports/${reportId}`);
      await loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    }
  }

  async function handleToggleActive(report: ScheduledReport) {
    try {
      await apiClient.put(`admin/scheduled-reports/${report.reportId}`, {
        isActive: !report.isActive,
      });
      await loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update report');
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  }

  function getStatusBadge(report: ScheduledReport): { label: string; className: string } {
    if (!report.isActive) return { label: 'Inactive', className: 'badge-gray' };
    if (report.lastDeliveryStatus === 'failure') return { label: 'Failed', className: 'badge-red' };
    if (report.lastDeliveryStatus === 'success') return { label: 'Active', className: 'badge-green' };
    return { label: 'Pending', className: 'badge-blue' };
  }

  if (loading) return <div ><p className="text-slate-400">Loading scheduled reports...</p></div>;

  return (
    <div className=" max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-text-primary">Scheduled Reports</h1>
        <button
          onClick={openCreateForm}
          className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90"
        >
          New Scheduled Report
        </button>
      </div>

      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Report Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            {editingReport ? 'Edit Scheduled Report' : 'Create Scheduled Report'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="reportName" className="block text-sm font-medium mb-1">Report Name</label>
              <input
                id="reportName"
                type="text"
                value={formData.reportName}
                onChange={e => setFormData(prev => ({ ...prev, reportName: e.target.value }))}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="reportType" className="block text-sm font-medium mb-1">Report Type</label>
                <select
                  id="reportType"
                  value={formData.reportType}
                  onChange={e => setFormData(prev => ({ ...prev, reportType: e.target.value as ReportType }))}
                  className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
                  disabled={!!editingReport}
                >
                  <option value="financial">Financial Report</option>
                  <option value="itSpend">IT Spend Report</option>
                </select>
              </div>

              <div>
                <label htmlFor="schedule" className="block text-sm font-medium mb-1">Schedule</label>
                <select
                  id="schedule"
                  value={formData.schedule}
                  onChange={e => setFormData(prev => ({ ...prev, schedule: e.target.value as ScheduleFrequency }))}
                  className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div>
                <label htmlFor="format" className="block text-sm font-medium mb-1">Format</label>
                <select
                  id="format"
                  value={formData.format}
                  onChange={e => setFormData(prev => ({ ...prev, format: e.target.value as ReportFormat }))}
                  className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
                >
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="recipients" className="block text-sm font-medium mb-1">
                Recipients (comma-separated emails)
              </label>
              <input
                id="recipients"
                type="text"
                value={formData.recipients}
                onChange={e => setFormData(prev => ({ ...prev, recipients: e.target.value }))}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
                placeholder="user@example.com, other@example.com"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingReport ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reports List */}
      {reports.length === 0 ? (
        <p className="text-slate-400">No scheduled reports configured.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border bg-page">
                <th className="text-left p-3 text-sm font-medium">Report Name</th>
                <th className="text-left p-3 text-sm font-medium">Type</th>
                <th className="text-left p-3 text-sm font-medium">Schedule</th>
                <th className="text-left p-3 text-sm font-medium">Format</th>
                <th className="text-left p-3 text-sm font-medium">Owner</th>
                <th className="text-left p-3 text-sm font-medium">Status</th>
                <th className="text-left p-3 text-sm font-medium">Last Delivery</th>
                <th className="text-left p-3 text-sm font-medium">Next Delivery</th>
                <th className="text-left p-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => {
                const status = getStatusBadge(report);
                return (
                  <tr key={report.reportId} className="border-b border-border hover:bg-page/50">
                    <td className="p-3 text-sm">{report.reportName}</td>
                    <td className="p-3 text-sm capitalize">{report.reportType}</td>
                    <td className="p-3 text-sm capitalize">{report.schedule}</td>
                    <td className="p-3 text-sm uppercase">{report.format}</td>
                    <td className="p-3 text-sm">{report.userEmail}</td>
                    <td className="p-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{formatDate(report.lastDeliveryAt)}</td>
                    <td className="p-3 text-sm">{formatDate(report.nextDeliveryAt)}</td>
                    <td className="p-3 text-sm">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditForm(report)}
                          className="px-2.5 py-1 text-xs rounded-lg border border-border bg-white text-slate-600 hover:bg-page"
                          aria-label={`Edit ${report.reportName}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(report)}
                          className="px-2.5 py-1 text-xs rounded-lg border border-border bg-white text-slate-600 hover:bg-page"
                          aria-label={`${report.isActive ? 'Pause' : 'Resume'} ${report.reportName}`}
                        >
                          {report.isActive ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          onClick={() => handleDelete(report.reportId)}
                          className="px-2.5 py-1 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label={`Delete ${report.reportName}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
